// Consentement aux cookies : le choix du visiteur, et les scripts qui en dépendent.
//
// Deux finalités soumises à accord (règles de la CNIL, article 82 de la loi
// Informatique et Libertés) :
//   - `analytics` : la mesure d'audience détaillée (PostHog avec cookies,
//     identification et enregistrements de session ; Metricool) ;
//   - `marketing` : la publicité et l'affiliation (Google Tag Manager, pixel et
//     API Conversions de Meta, cookie d'attribution des affiliés).
//
// Ce qui ne demande pas d'accord ne passe pas par ici : la connexion, la
// sécurité (cookie blvmy_device), ce cookie de choix lui-même, et la mesure
// d'audience ANONYME de PostHog (`cookieless_mode: "on_reject"` dans
// app/[locale]/provider.jsx), qui compte les visites sans rien déposer tant que
// le visiteur n'a pas accepté.
//
// Le choix vit dans un cookie lisible par le navigateur, gardé six mois comme
// le recommande la CNIL : au-delà, la carte se représente. Changer la
// structure du choix ou la liste des finalités impose d'incrémenter
// CONSENT_VERSION, pour redemander à tout le monde.

export const CONSENT_COOKIE = "blvmy_consent";
export const CONSENT_VERSION = 1;
const CONSENT_MAX_AGE = 60 * 60 * 24 * 182; // six mois

/** Événement émis à chaque choix enregistré (detail = le choix). */
export const CONSENT_CHANGE_EVENT = "blvmy:consent-change";
/** Événement qui rouvre la carte sur le détail des finalités. */
export const CONSENT_OPEN_EVENT = "blvmy:consent-open";

/**
 * Le choix enregistré, ou null si le visiteur n'a encore rien décidé (ou si
 * la version a changé depuis son choix).
 *
 * @returns {{ analytics: boolean, marketing: boolean, date: number, version: number } | null}
 */
export function readConsent() {
    return parseConsent(readConsentRaw());
}

/** La valeur brute du cookie de choix ("" s'il n'existe pas). */
export function readConsentRaw() {
    if (typeof document === "undefined") return "";
    const part = document.cookie
        .split("; ")
        .find((item) => item.startsWith(`${CONSENT_COOKIE}=`));
    return part ? part.slice(CONSENT_COOKIE.length + 1) : "";
}

/** Lit une valeur brute du cookie de choix (voir readConsent). */
export function parseConsent(raw) {
    if (!raw) return null;
    try {
        const value = JSON.parse(decodeURIComponent(raw));
        if (!value || value.v !== CONSENT_VERSION) return null;
        return {
            analytics: value.a === 1,
            marketing: value.m === 1,
            date: value.d || null,
            version: value.v,
        };
    } catch (error) {
        // Cookie illisible : on redemande.
        return null;
    }
}

/**
 * Enregistre le choix et prévient la page (scripts, PostHog, pixel).
 *
 * @param {{ analytics: boolean, marketing: boolean }} choice
 */
export function writeConsent({ analytics, marketing }) {
    if (typeof document === "undefined") return null;
    const value = {
        v: CONSENT_VERSION,
        a: analytics ? 1 : 0,
        m: marketing ? 1 : 0,
        d: Math.floor(Date.now() / 1000),
    };
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(
        JSON.stringify(value),
    )}; max-age=${CONSENT_MAX_AGE}; path=/; SameSite=Lax${secure}`;

    const consent = readConsent();
    window.dispatchEvent(
        new CustomEvent(CONSENT_CHANGE_EVENT, { detail: consent }),
    );
    return consent;
}

/** Le visiteur a-t-il accepté cette finalité ? */
export function hasConsent(purpose) {
    return readConsent()?.[purpose] === true;
}

/**
 * Aligne PostHog sur le choix du visiteur.
 *
 * PostHog tourne en `cookieless_mode: "on_reject"` : tant que le choix est en
 * attente ou refusé, il compte les visites sans cookie ni identification ;
 * l'accord « mesure d'audience » rallume les cookies, l'identification et les
 * enregistrements de session. PostHog garde sa propre trace du choix (un an) :
 * on la remet d'aplomb à chaque chargement, notre cookie faisant foi.
 */
export function syncPostHogConsent(posthog, consent) {
    if (!posthog || !posthog.__loaded) return;
    try {
        const status = posthog.get_explicit_consent_status();
        if (consent?.analytics) {
            if (status !== "granted") {
                posthog.opt_in_capturing({ captureEventName: false });
            }
        } else if (consent) {
            if (status !== "denied") posthog.opt_out_capturing();
        } else if (status !== "pending") {
            posthog.clear_opt_in_out_capturing();
        }
    } catch (error) {
        // Un outil de mesure en erreur ne doit jamais casser la page.
    }
}

/**
 * Efface les cookies publicitaires posés sur notre domaine quand l'accord
 * « publicité » est retiré (pixel Meta, Google Ads, attribution affiliée).
 */
export function clearMarketingCookies() {
    if (typeof document === "undefined") return;
    const names = ["_fbp", "_fbc", "_gcl_au", "blvmy_ref", "blvmy_ref_tag"];
    const host = window.location.hostname;
    const domains = ["", host, `.${host.replace(/^www\./, "")}`];
    for (const name of names) {
        for (const domain of domains) {
            document.cookie = `${name}=; max-age=0; path=/${
                domain ? `; domain=${domain}` : ""
            }`;
        }
    }
}

/** Rouvre la carte de consentement (lien « Gérer les cookies » du pied de page). */
export function openConsentPreferences() {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(CONSENT_OPEN_EVENT));
}

// ---------------------------------------------------------------------------
// Scripts tiers, chargés seulement après accord. Chaque chargeur est
// idempotent : il ne s'exécute qu'une fois par page.
// ---------------------------------------------------------------------------

const GTM_ID = "GTM-TGC9PKNX";

function injectScript(id, src) {
    if (document.getElementById(id)) return;
    const script = document.createElement("script");
    script.id = id;
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
}

function gtag() {
    window.dataLayer = window.dataLayer || [];
    // gtag passe l'objet `arguments` tel quel : c'est ce que GTM attend.
    window.dataLayer.push(arguments);
}

function consentModeState(consent) {
    const ads = consent?.marketing ? "granted" : "denied";
    return {
        ad_storage: ads,
        ad_user_data: ads,
        ad_personalization: ads,
        analytics_storage: consent?.analytics ? "granted" : "denied",
    };
}

/**
 * Google Tag Manager, seulement avec l'accord « publicité » : le conteneur
 * porte les conversions Google Ads. Le mode consentement de Google reçoit
 * l'état réel avant le chargement, pour que les balises Google le respectent
 * aussi pour la mesure d'audience.
 */
function loadGoogleTagManager(consent) {
    if (window.__blvmyGtmLoaded) {
        gtag("consent", "update", consentModeState(consent));
        return;
    }
    window.__blvmyGtmLoaded = true;
    gtag("consent", "default", consentModeState(consent));
    window.dataLayer.push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
    });
    injectScript(
        "gtm-script",
        `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`,
    );
}

/**
 * Le SDK du pixel Meta. Le stub `fbq` est posé tout de suite (il met les
 * appels en file jusqu'au chargement), et FacebookPixel.jsx fait l'init.
 */
function loadMetaPixel() {
    if (window.fbq) return;
    !(function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
            n.callMethod
                ? n.callMethod.apply(n, arguments)
                : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = "2.0";
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
    })(
        window,
        document,
        "script",
        "https://connect.facebook.net/en_US/fbevents.js",
    );
}

/** Le suivi de Metricool (mesure d'audience des visites venues des réseaux). */
function loadMetricool() {
    if (document.getElementById("metricool-script")) return;
    const script = document.createElement("script");
    script.id = "metricool-script";
    script.async = true;
    script.src = "https://tracker.metricool.com/resources/be.js";
    script.onload = () => {
        try {
            window.beTracker?.t({ hash: "f5e7802586ae71f3a6fdc1aaa686a6a8" });
            window.beTracker?.t({ hash: "b4e9a58bf81bdc0bc5879cc63d37b966" });
        } catch (error) {
            // Un traceur en erreur ne doit jamais casser la page.
        }
    };
    document.head.appendChild(script);
}

/**
 * Charge ce que le choix autorise. Appelé au chargement de la page et à
 * chaque nouveau choix. Retirer un accord déjà donné recharge la page (voir
 * CookieConsent.jsx) : un script chargé ne se décharge pas.
 */
export function applyConsentScripts(consent) {
    if (typeof window === "undefined" || !consent) return;
    try {
        if (consent.marketing) {
            loadGoogleTagManager(consent);
            loadMetaPixel();
        }
        if (consent.analytics) {
            loadMetricool();
        }
    } catch (error) {
        // Un traceur en erreur ne doit jamais casser la page.
    }
}
